import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Auth0Context,
  Auth0ContextInterface,
  initialContext,
} from '@auth0/auth0-react';
import type { GetTokenSilentlyVerboseResponse } from '@auth0/auth0-spa-js';
import { jwtDecode } from 'jwt-decode';
import { makeStyles } from 'tss-react/mui';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { Auth0RopgTokens } from '@shared/models/auth0-ropg.model';
import {
  clearStoredLocalhostRopgTokens,
  isStoredAccessTokenFresh,
  LocalhostRopgApiUnreachableError,
  LocalhostRopgNotConfiguredError,
  localhostRopgFailureCopy,
  readStoredLocalhostRopgTokens,
  requestLocalhostRopgTokens,
  skipLocalhostRopg,
  StoredAuth0RopgTokens,
  userFromIdToken,
  writeStoredLocalhostRopgTokens,
} from '../helpers/auth0-localhost-ropg.helpers';

const loadingContext: Auth0ContextInterface = {
  ...initialContext,
  isLoading: true,
  isAuthenticated: false,
  // initialContext.loginWithRedirect throws; the HOC must not hit Auth0 while ROPG runs.
  loginWithRedirect: () => Promise.resolve(),
};

/**
 * {@link Auth0ContextInterface.getAccessTokenSilently} with the SDK overloads.
 */
const createGetAccessTokenSilently = (
  refreshTokens: () => Promise<StoredAuth0RopgTokens>,
  tokensRef: { current: StoredAuth0RopgTokens },
): Auth0ContextInterface['getAccessTokenSilently'] => {
  async function getAccessTokenSilently(options: {
    detailedResponse: true;
  }): Promise<GetTokenSilentlyVerboseResponse>;
  async function getAccessTokenSilently(options?: object): Promise<string>;
  async function getAccessTokenSilently(options?: {
    detailedResponse?: boolean;
    cacheMode?: string;
  }): Promise<string | GetTokenSilentlyVerboseResponse> {
    let current = tokensRef.current;
    const cacheOff = options?.cacheMode === 'off';
    if (cacheOff || !isStoredAccessTokenFresh(current)) {
      current = await refreshTokens();
    }
    if (options?.detailedResponse) {
      return {
        access_token: current.access_token,
        id_token: current.id_token,
        expires_in: current.expires_in,
        token_type: current.token_type,
      };
    }
    return current.access_token;
  }
  return getAccessTokenSilently;
};

type Auth0LocalhostRopgGateProps = {
  apiUrl: string;
  children: ReactNode;
  fallback: ReactNode;
};

type RopgGateState =
  | { status: 'loading' }
  | { status: 'ready'; tokens: StoredAuth0RopgTokens }
  | { status: 'fallback' }
  | { status: 'error'; error: unknown };

const useFailureStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(3),
  },
  title: {
    margin: 0,
  },
  detail: {
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
}));

/**
 * Visible failure when localhost auto-login stops.
 * Does not mount Universal Login.
 */
const LocalhostRopgFailureView: FC<{ error: unknown }> = ({ error }) => {
  const { classes } = useFailureStyles();
  const copy = localhostRopgFailureCopy(error);
  return (
    <div className={classes.root} role="alert">
      <p className={classes.title}>{copy.title}</p>
      <p className={classes.detail}>{copy.detail}</p>
    </div>
  );
};

/**
 * Tries localhost ROPG. Universal Login is used when the grant is not
 * configured ({@link LocalhostRopgNotConfiguredError}). Other failures stay
 * on localhost so a working grant is not overwritten by Auth0's hosted login.
 */
export const Auth0LocalhostRopgGate: FC<Auth0LocalhostRopgGateProps> = ({
  apiUrl,
  children,
  fallback,
}) => {
  const [state, setState] = useState<RopgGateState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    const applyTokens = (tokens: Auth0RopgTokens): boolean => {
      if (!userFromIdToken(tokens.id_token)) return false;
      const stored = writeStoredLocalhostRopgTokens(tokens);
      if (!cancelled) setState({ status: 'ready', tokens: stored });
      return true;
    };

    const failWithoutHostedLogin = (error: unknown): void => {
      const apiUnreachable = error instanceof LocalhostRopgApiUnreachableError;
      console.error(
        apiUnreachable
          ? 'Localhost API is not reachable; Auth0 was not contacted'
          : 'Localhost Auth0 ROPG failed; not redirecting to Universal Login',
        error,
      );
      if (!cancelled) setState({ status: 'error', error });
    };

    const run = async (): Promise<void> => {
      const stored = readStoredLocalhostRopgTokens();
      if (stored && isStoredAccessTokenFresh(stored)) {
        if (userFromIdToken(stored.id_token)) {
          if (!cancelled) setState({ status: 'ready', tokens: stored });
          return;
        }
        clearStoredLocalhostRopgTokens();
      }

      try {
        if (
          applyTokens(
            await requestLocalhostRopgTokens(apiUrl, stored?.refresh_token),
          )
        ) {
          return;
        }
        failWithoutHostedLogin(new Error('id_token could not be parsed'));
      } catch (firstError) {
        if (firstError instanceof LocalhostRopgNotConfiguredError) {
          if (!cancelled) setState({ status: 'fallback' });
          return;
        }
        if (stored?.refresh_token) {
          try {
            if (applyTokens(await requestLocalhostRopgTokens(apiUrl))) return;
          } catch (secondError) {
            if (secondError instanceof LocalhostRopgNotConfiguredError) {
              if (!cancelled) setState({ status: 'fallback' });
              return;
            }
            failWithoutHostedLogin(secondError);
            return;
          }
        }
        failWithoutHostedLogin(firstError);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  switch (state.status) {
    case 'loading':
      return (
        <Auth0Context.Provider value={loadingContext}>
          {children}
        </Auth0Context.Provider>
      );
    case 'fallback':
      return <>{fallback}</>;
    case 'error':
      return <LocalhostRopgFailureView error={state.error} />;
    case 'ready':
      return (
        <Auth0LocalhostRopgProvider apiUrl={apiUrl} tokens={state.tokens}>
          {children}
        </Auth0LocalhostRopgProvider>
      );
    default:
      return assertUnreachable(state);
  }
};

type Auth0LocalhostRopgProviderProps = {
  apiUrl: string;
  tokens: StoredAuth0RopgTokens;
  children: ReactNode;
};

/**
 * Auth0 context backed by tokens from the localhost ROPG endpoint.
 */
const Auth0LocalhostRopgProvider: FC<Auth0LocalhostRopgProviderProps> = ({
  apiUrl,
  tokens: initialTokens,
  children,
}) => {
  const [tokens, setTokens] = useState(initialTokens);
  const tokensRef = useRef(tokens);
  tokensRef.current = tokens;

  const refreshTokens =
    useCallback(async (): Promise<StoredAuth0RopgTokens> => {
      const next = await requestLocalhostRopgTokens(
        apiUrl,
        tokensRef.current.refresh_token,
      );
      const stored = writeStoredLocalhostRopgTokens(next);
      tokensRef.current = stored;
      setTokens(stored);
      return stored;
    }, [apiUrl]);

  const getAccessTokenSilently = useMemo(
    () => createGetAccessTokenSilently(refreshTokens, tokensRef),
    [refreshTokens],
  );

  const user = userFromIdToken(tokens.id_token);

  const logout = useCallback<Auth0ContextInterface['logout']>(
    async (options) => {
      skipLocalhostRopg();
      clearStoredLocalhostRopgTokens();
      const returnTo =
        options && 'logoutParams' in options && options.logoutParams?.returnTo
          ? options.logoutParams.returnTo
          : window.location.origin;
      window.location.assign(returnTo);
    },
    [],
  );

  const value = useMemo<Auth0ContextInterface>(
    () => ({
      ...initialContext,
      isAuthenticated: Boolean(user?.sub),
      isLoading: false,
      user,
      getAccessTokenSilently,
      getIdTokenClaims: async () => jwtDecode(tokensRef.current.id_token),
      logout,
      loginWithRedirect: () => Promise.resolve(),
    }),
    [getAccessTokenSilently, logout, user],
  );

  return (
    <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>
  );
};
